import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import type { VehicleDef } from "@hyperlight/shared";
import type { GroundVehicle } from "./groundVehicle";
import { ProceduralSpeederView, type VehicleView } from "./proceduralSpeeder";

/** Authored-mesh chassis placement: how a shipped GLB's own frame maps onto the
 * chassis frame every VehicleDef is authored in (forward −Z, up +Y, origin at
 * the hull centre). Kept as data next to the model list so a new model is one
 * entry plus a URL, not a new view class. */
export interface GlbChassisDef {
  url: string;
  /** Uniform metres-per-model-unit. */
  scale: number;
  /** Yaw applied before scale, radians. Maps the model's long axis onto ±Z. */
  yaw: number;
  /** Chassis-local offset applied after scale, so the model's own centre can sit
   * off the origin the probes and seats are authored against. */
  offset: readonly [number, number, number];
  /** Rear exhaust sockets in CHASSIS space (post-transform). Glow discs are
   * placed here and drive the boost trails. */
  exhausts: readonly (readonly [number, number, number])[];
  /** Radius of each exhaust glow disc. */
  exhaustRadius: number;
}

/** Every vehicle whose chassis is an authored GLB rather than procedural boxes.
 * The hover bike's source export is 1.9 model units long with its nose at −X and
 * up already +Y, so it yaws a quarter turn onto the chassis' −Z forward.
 *
 * These models are LOW-POLY and carry no textures at all: colour lives in vertex
 * attributes and NORMAL is deliberately absent, so GLTFLoader flat-shades them.
 * Both are AUTHORED parametrically — build-hover-bike.mts and
 * build-scout-speeder.mts, sharing the kit in client/test/lib/hardSurface.mts —
 * rather than decimated from a dense export, which is what convert-vehicle.mts
 * used to do and its header explains why it stopped. Nothing here needs to know:
 * the loader wires vertexColors and flatShading on its own. */
export const GLB_CHASSIS: Readonly<Record<string, GlbChassisDef>> = {
  // Replaces the procedural box shell the scout used to wear. Nose points -X in
  // the export (the emissive engine nacelles sit at +X, which is what settles
  // which end is the tail), so the same quarter turn as the bike.
  "scout-speeder": {
    url: "/models/vehicles/scout-speeder.glb",
    scale: 2.15,
    yaw: -Math.PI / 2,
    offset: [0, 0, 0],
    // ONE emitter, on the centreline and slung low. The speeder used to carry a
    // pair of nozzles either side of a flat transom and this was a matching pair;
    // it now has a single large thruster under a tapered boat-tail (see
    // build-scout-speeder.mts), so a pair would hang two glow discs in mid-air.
    exhausts: [[0, -0.14, 1.95]],
    exhaustRadius: 0.26,
  },
  // First flyer. One engine, dead astern — the emitter doubles as the flight
  // thrust cue, so it sits exactly in the authored bore (build-m66-stingray.mts
  // prints the landmark every build).
  "m66-stingray": {
    url: "/models/vehicles/m66-stingray.glb",
    scale: 3.7,
    yaw: -Math.PI / 2,
    offset: [0, 0, 0],
    exhausts: [[0, -0.07, 3.4]],
    exhaustRadius: 0.55,
  },
  // Second flyer — one engine astern, same seam as the Stingray.
  "banshee": {
    url: "/models/vehicles/banshee.glb",
    scale: 3.0,
    yaw: -Math.PI / 2,
    offset: [0, 0, 0],
    exhausts: [[0, 0, 2.6]],
    exhaustRadius: 0.45,
  },
  "hover-bike": {
    url: "/models/vehicles/hover-bike.glb",
    scale: 1.7,
    yaw: -Math.PI / 2,
    offset: [0, 0, 0],
    exhausts: [[-0.24, 0.0, 1.52], [0.24, 0.0, 1.52]],
    exhaustRadius: 0.16,
  },
};

/** One in-flight load per URL, shared by the local player's chassis and every
 * remote rider's: the parsed scene is cloned per view (geometry and materials
 * stay shared), so a crowded street costs one download and one upload. */
const loads = new Map<string, Promise<THREE.Object3D>>();

function loadChassis(url: string): Promise<THREE.Object3D> {
  let pending = loads.get(url);
  if (!pending) {
    pending = new GLTFLoader().loadAsync(url).then((gltf) => {
      gltf.scene.traverse((node) => {
        if (!(node instanceof THREE.Mesh)) return;
        node.castShadow = true;
        node.receiveShadow = true;
      });
      return gltf.scene;
    });
    loads.set(url, pending);
  }
  return pending;
}

/**
 * Renders an authored GLB chassis. Interchangeable with ProceduralSpeederView:
 * gameplay only ever touches the VehicleView interface, so a vehicle's look can
 * be a mesh or a box pile without the simulation knowing which.
 *
 * The model streams in asynchronously; until it lands the group is simply empty
 * (the vehicle is still fully drivable — this is presentation only).
 */
export class GlbVehicleView implements VehicleView {
  readonly group = new THREE.Group();
  private readonly body = new THREE.Group();
  private readonly thrusterGlows: THREE.Mesh[] = [];
  private readonly thruster: THREE.MeshBasicMaterial;
  private readonly geometries: THREE.BufferGeometry[] = [];
  private disposed = false;
  private bank = 0;
  private readonly right = new THREE.Vector3();

  constructor(readonly def: VehicleDef, chassis: GlbChassisDef) {
    this.group.add(this.body);
    this.group.name = `vehicle:${def.id}`;
    this.thruster = new THREE.MeshBasicMaterial({ color: 0x56d9ff, transparent: true, opacity: 0.6, toneMapped: false });

    // The authored mesh carries its own emissive strips as a separate primitive;
    // these discs are the boost-trail emitters and the speed-tracked afterburner.
    for (const point of chassis.exhausts) {
      const geometry = new THREE.CylinderGeometry(chassis.exhaustRadius * 0.78, chassis.exhaustRadius, 0.14, 12);
      this.geometries.push(geometry);
      const glow = new THREE.Mesh(geometry, this.thruster);
      glow.position.set(...point);
      glow.rotation.x = Math.PI / 2;
      this.body.add(glow);
      this.thrusterGlows.push(glow);
    }

    void loadChassis(chassis.url).then((scene) => {
      // dispose() can beat the download home (spawn then immediately replace).
      if (this.disposed) return;
      const model = scene.clone(true);
      model.scale.setScalar(chassis.scale);
      model.rotation.y = chassis.yaw;
      model.position.set(...chassis.offset);
      this.body.add(model);
    }).catch((err: unknown) => {
      console.warn(`[vehicles] chassis load failed: ${chassis.url}`, err);
    });
  }

  update(vehicle: GroundVehicle, dt: number): void {
    // Same cosmetic lean + speed/boost-tracked exhaust as the procedural
    // speeder, so both chassis read identically under the same inputs.
    // GROUND ONLY. Airborne, the flight controller already banks the real
    // chassis into the turn, and sideslip bleeds off through QUADRATIC side
    // drag — so this cue would stack a second roll on top of the first and
    // then creep back for tens of seconds after the stick centred, which
    // reads as "the ship never levels out".
    const side = vehicle.gliding ? 0 : vehicle.vel.dot(this.right.set(1, 0, 0).applyQuaternion(vehicle.quat));
    const targetBank = Math.max(-0.24, Math.min(0.24, -side * 0.02));
    this.bank += (targetBank - this.bank) * (1 - Math.exp(-7 * dt));
    this.body.rotation.z = this.bank;
    const speedK = Math.min(1, Math.abs(vehicle.forwardSpeed) / Math.max(1, vehicle.def.handling.maxSpeed));
    this.thruster.opacity = Math.min(1, 0.22 + speedK * 0.4 + vehicle.boostK * 0.5);
  }

  rearExhaustPoints(out: THREE.Vector3[]): number {
    for (let i = 0; i < this.thrusterGlows.length; i++) {
      if (!out[i]) out[i] = new THREE.Vector3();
      this.thrusterGlows[i].getWorldPosition(out[i]);
    }
    return this.thrusterGlows.length;
  }

  dispose(): void {
    this.disposed = true;
    this.group.removeFromParent();
    // The cloned model shares the cached scene's geometry/materials — only this
    // view's own glow resources are ours to release.
    for (const geometry of this.geometries) geometry.dispose();
    this.thruster.dispose();
  }
}

/** The one place that decides how a vehicle id is rendered. Gameplay, the god
 * panel, and remote players all go through here so a chassis never depends on
 * which code path spawned it. `hue` only reaches the procedural shell — an
 * authored model wears its own textures. */
export function createVehicleView(def: VehicleDef, hue?: number): VehicleView {
  const chassis = GLB_CHASSIS[def.id];
  return chassis ? new GlbVehicleView(def, chassis) : new ProceduralSpeederView(def, hue);
}
